import { useCallback, useEffect, useState } from 'react';

import type { Role } from '../../core/policy/roles';
import type { CommitteeMember } from '../../features/committee/application/ports/rolesPort';
import { RolesPage } from '../../pages/RolesPage';

import { useServices } from '../providers/servicesContext';

/**
 * Connects the permissions screen to its port.
 *
 * `employee` is not offered: it is what everyone linked to a record already is,
 * and granting it explicitly would suggest it could be withheld.
 */
const ASSIGNABLE: readonly Role[] = [
  'committee_hr',
  'committee_finance',
  'committee_manager',
];

export function RolesScreen({ ownUid }: { readonly ownUid: string }) {
  const { roles: rolesPort } = useServices();

  const [members, setMembers] = useState<readonly CommitteeMember[]>([]);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [noticeAr, setNoticeAr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    rolesPort
      .listLinkedAccounts()
      .then((accounts) => {
        if (!cancelled) setMembers(accounts);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل الحسابات.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [rolesPort, reloadToken]);

  const toggle = useCallback(
    (member: CommitteeMember, role: Role) => {
      const next = member.roles.includes(role)
        ? member.roles.filter((held) => held !== role)
        : [...member.roles, role];

      setBusyUid(member.authUid);
      setErrorAr(null);
      setNoticeAr(null);

      void rolesPort
        .setRoles(member.authUid, next)
        .then(() => {
          setNoticeAr('حُفظت الصلاحيات. تسري بعد أن يسجّل صاحبها خروجًا ودخولًا.');
          // Re-read rather than patch: the function decides, and a locally
          // edited copy would claim a change this screen only requested.
          setReloadToken((token) => token + 1);
        })
        .catch((error: unknown) => {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر حفظ الصلاحيات.');
        })
        .finally(() => {
          setBusyUid(null);
        });
    },
    [rolesPort],
  );

  return (
    <RolesPage
      members={members}
      assignableRoles={ASSIGNABLE}
      ownUid={ownUid}
      busyUid={busyUid}
      errorAr={errorAr}
      noticeAr={noticeAr}
      onToggleRole={toggle}
    />
  );
}
